import React, { useState } from 'react';
import { ChevronLeft, ChevronRight, Edit3, Plus } from 'lucide-react';
import {
  addDays,
  entryHours,
  fmtDur,
  fmtTime,
  isoDate,
  mondayOf,
  parseIsoDate,
} from '../lib/time.js';
import { validIso } from '../lib/dateInput.js';
import {
  sumByWorkType,
  workTypeLabel as typeLabel,
  workTypeOf,
} from '../lib/workTypes.js';
import { HoursBreakdown } from '../components/HoursBreakdown.jsx';
import { useSessionState } from '../lib/useSessionState.js';
import { useHolidays } from '../lib/useHolidays.js';
import {
  flashRowProps,
  ProjectLabel,
  Table,
  useSortedRows,
} from '../components/ui.jsx';
import { PageHeader } from '../components/PageSkeleton.jsx';

const VIEW_STORAGE_KEY = 'tt_timesheet_view';
const GROUP_SORT_STORAGE_KEY = 'tt_timesheet_group_sort';
const PERIOD_STORAGE_KEY = 'tt_timesheet_period';
const PERIOD_MODES = ['day', 'week', 'month'];
const ENTRY_HEADERS = [
  { label: 'Date & time', sortKey: 'date' },
  { label: 'Project', sortKey: 'project' },
  { label: 'Work type', sortKey: 'workType' },
  { label: 'Description', sortKey: 'description' },
  { label: 'Duration', sortKey: 'duration' },
  '',
];

function readStoredView() {
  return window.localStorage.getItem(VIEW_STORAGE_KEY) === 'flat'
    ? 'flat'
    : 'grouped';
}

function readStoredPeriod() {
  const stored = window.localStorage.getItem(PERIOD_STORAGE_KEY);
  return PERIOD_MODES.includes(stored) ? stored : 'week';
}

function readStoredGroupDir() {
  return window.localStorage.getItem(GROUP_SORT_STORAGE_KEY) === 'asc'
    ? 'asc'
    : 'desc';
}

// Long weekday name for a YYYY-MM-DD string, parsed as local midnight.
function dayName(workDate) {
  return new Date(`${workDate}T00:00:00`).toLocaleDateString('en-US', {
    weekday: 'long',
  });
}

// Group entries into days (newest day first), each day's entries ordered by
// start time per groupDir ('asc' or 'desc'), plus the day's total hours.
function groupByDay(entries, groupDir) {
  const days = new Map();
  for (const entry of entries) {
    if (!days.has(entry.workDate)) days.set(entry.workDate, []);
    days.get(entry.workDate).push(entry);
  }
  const sign = groupDir === 'asc' ? 1 : -1;
  return [...days.entries()]
    .sort((a, b) => (a[0] < b[0] ? 1 : -1))
    .map(([date, items]) => ({
      date,
      items: [...items].sort((a, b) => sign * (a.start - b.start)),
      total: items.reduce((sum, entry) => sum + entryHours(entry), 0),
    }));
}

export function Timesheet({ data, openEntry, openQuickAdd }) {
  const [periodMode, setPeriodMode] = useState(readStoredPeriod);
  // The day the shown period is anchored on, remembered for the tab session
  // so leaving the Timesheet and coming back keeps the week you were on.
  const [anchorIso, setAnchorIso] = useSessionState(
    'timesheet.anchor',
    isoDate(new Date()),
    (value, fallback) => validIso(value) || fallback,
  );
  const anchor = parseIsoDate(anchorIso);
  const [viewMode, setViewMode] = useState(readStoredView);
  const [groupDir, setGroupDir] = useState(readStoredGroupDir);
  const [groupFlashToken, setGroupFlashToken] = useState(0);
  const isWeek = periodMode === 'week';
  const isDay = periodMode === 'day';
  const dayStart = new Date(
    anchor.getFullYear(),
    anchor.getMonth(),
    anchor.getDate(),
  );
  const rangeStart = isDay
    ? dayStart
    : isWeek
      ? mondayOf(anchor)
      : new Date(anchor.getFullYear(), anchor.getMonth(), 1);
  const rangeEnd = isDay
    ? dayStart
    : isWeek
      ? addDays(rangeStart, 6)
      : new Date(anchor.getFullYear(), anchor.getMonth() + 1, 0);
  const rangeStartStr = isoDate(rangeStart);
  const rangeEndStr = isoDate(rangeEnd);
  const rangeLabel =
    rangeStartStr === rangeEndStr
      ? rangeStartStr
      : `${rangeStartStr} to ${rangeEndStr}`;
  const filteredRows = data.entries.filter(
    (entry) => entry.workDate >= rangeStartStr && entry.workDate <= rangeEndStr,
  );

  function resolveProject(entry) {
    return (
      data.allProjects.find((item) => item.id === entry.projectId) ||
      data.projects.find((item) => item.id === entry.projectId) || {
        code: entry.projectCode,
        name: entry.projectName,
        color: entry.color,
      }
    );
  }

  function workTypeLabel(entry, project) {
    return typeLabel(workTypeOf(project, entry));
  }

  function entryRowValue(entry, key) {
    const project = resolveProject(entry);
    if (key === 'date')
      return new Date(entry.workDate).getTime() + entry.start * 3600000;
    if (key === 'project') return `${project.name} ${project.code}`;
    if (key === 'workType') return workTypeLabel(entry, project);
    if (key === 'description') return entry.description || '';
    return entryHours(entry);
  }

  const [rows, sort, sortBy, sortFlashToken] = useSortedRows(
    filteredRows,
    entryRowValue,
    { key: 'date', direction: 'desc' },
    'timesheet.entries',
    ENTRY_HEADERS,
  );
  // Total is work plus paid absence; unpaid absence is shown apart (a day's
  // own total below still counts everything, so a day off reads as filled).
  const hours = sumByWorkType(filteredRows, [
    ...data.projects,
    ...data.allProjects,
  ]);
  const groupedDays = groupByDay(filteredRows, groupDir);
  const holidays = useHolidays(data.me.id, rangeStartStr, rangeEndStr);
  const todayStr = isoDate(new Date());

  function movePeriod(delta) {
    setAnchorIso((currentIso) => {
      const current = parseIsoDate(currentIso);
      if (isDay) return isoDate(addDays(current, delta));
      if (isWeek) return isoDate(addDays(current, delta * 7));
      return isoDate(
        new Date(current.getFullYear(), current.getMonth() + delta, 1),
      );
    });
  }

  function changeView(next) {
    setViewMode(next);
    window.localStorage.setItem(VIEW_STORAGE_KEY, next);
  }

  function changePeriod(next) {
    setPeriodMode(next);
    window.localStorage.setItem(PERIOD_STORAGE_KEY, next);
  }

  function toggleGroupDir() {
    setGroupDir((current) => {
      const next = current === 'asc' ? 'desc' : 'asc';
      window.localStorage.setItem(GROUP_SORT_STORAGE_KEY, next);
      return next;
    });
    setGroupFlashToken((n) => n + 1);
  }

  function entryRow(entry, showDate, flashToken) {
    const project = resolveProject(entry);
    const { key, className } = flashRowProps(entry.id, flashToken, 'clickable');
    return (
      <tr key={key} className={className} onClick={() => openEntry(entry)}>
        <td className="mono">
          {showDate && `${entry.workDate} · `}
          {fmtTime(entry.start)}-{fmtTime(entry.end)}
        </td>
        <td>
          <ProjectLabel project={project} />
        </td>
        <td>{workTypeLabel(entry, project)}</td>
        <td>
          <span className="truncate table-description">
            {entry.description}
          </span>
        </td>
        <td className="strong">{fmtDur(entryHours(entry))}</td>
        <td>
          <button
            className="icon-button"
            onClick={(event) => {
              event.stopPropagation();
              openEntry(entry);
            }}
          >
            <Edit3 size={15} />
          </button>
        </td>
      </tr>
    );
  }

  return (
    <div>
      <PageHeader
        title="Timesheet"
        subtitle={`${rangeLabel} · ${filteredRows.length} entries`}
        actions={
          <div className="page-actions">
            <div className="segmented inline" aria-label="Group entries">
              <button
                className={viewMode === 'grouped' ? 'active' : ''}
                onClick={() => changeView('grouped')}
              >
                Grouped
              </button>
              <button
                className={viewMode === 'flat' ? 'active' : ''}
                onClick={() => changeView('flat')}
              >
                Flat
              </button>
            </div>
            <div className="segmented inline" aria-label="Change period">
              <button
                className={periodMode === 'day' ? 'active' : ''}
                onClick={() => changePeriod('day')}
              >
                Day
              </button>
              <button
                className={periodMode === 'week' ? 'active' : ''}
                onClick={() => changePeriod('week')}
              >
                Week
              </button>
              <button
                className={periodMode === 'month' ? 'active' : ''}
                onClick={() => changePeriod('month')}
              >
                Month
              </button>
            </div>
            <div
              className="segmented inline calendar-stepper"
              aria-label={`Change ${periodMode}`}
            >
              <button
                onClick={() => movePeriod(-1)}
                data-tooltip={`Previous ${periodMode}`}
                aria-label={`Previous ${periodMode}`}
              >
                <ChevronLeft size={14} />
              </button>
              <button
                onClick={() => movePeriod(1)}
                data-tooltip={`Next ${periodMode}`}
                aria-label={`Next ${periodMode}`}
              >
                <ChevronRight size={14} />
              </button>
            </div>
            <button className="primary" onClick={() => openQuickAdd({})}>
              <Plus size={14} /> Add entry
            </button>
          </div>
        }
      />
      <HoursBreakdown hours={hours} />
      {viewMode === 'flat' ? (
        <Table headers={ENTRY_HEADERS} sort={sort} onSort={sortBy}>
          {rows.map((entry) => entryRow(entry, true, sortFlashToken))}
        </Table>
      ) : (
        <Table
          headers={[
            { label: 'Time', sortKey: 'time' },
            'Project',
            'Work type',
            'Description',
            'Duration',
            '',
          ]}
          sort={{ key: 'time', direction: groupDir }}
          onSort={toggleGroupDir}
        >
          {groupedDays.map((group) => (
            <React.Fragment key={group.date}>
              <tr className="day-row">
                <td colSpan={6}>
                  <div className="day-band">
                    <div className="day-left">
                      <span className="day-name">{dayName(group.date)}</span>
                      <span className="day-date">{group.date}</span>
                      {group.date === todayStr && (
                        <span className="today-pill">Today</span>
                      )}
                      {holidays.has(group.date) && (
                        <span className="holiday-pill">
                          {holidays.get(group.date)}
                        </span>
                      )}
                    </div>
                    <div className="day-right">
                      <span className="day-total">{fmtDur(group.total)}</span>
                      <span className="day-meta">
                        {group.items.length}{' '}
                        {group.items.length === 1 ? 'entry' : 'entries'} ·{' '}
                        {(group.total / 8).toFixed(2)} MD
                      </span>
                      <button
                        className="day-add"
                        data-tooltip={`Add entry on ${group.date}`}
                        aria-label={`Add entry on ${group.date}`}
                        onClick={() => openQuickAdd({ workDate: group.date })}
                      >
                        <Plus size={15} />
                      </button>
                    </div>
                  </div>
                </td>
              </tr>
              {group.items.map((entry) =>
                entryRow(entry, false, groupFlashToken),
              )}
            </React.Fragment>
          ))}
        </Table>
      )}
    </div>
  );
}
